/**
 * The digit boundary, in one place.
 *
 * Human surfaces render Arabic-Indic `٠١٢٣٤٥٦٧٨٩`; machine surfaces —
 * `<time datetime>`, `aria-valuenow`, every `data-*`, every CSS custom
 * property a stylesheet reads a number out of, hidden input values, every API
 * payload — carry ASCII. **Never auto-convert between the two layers**: the
 * formatter emits the right codepoints at the render site.
 *
 * A configuration value is a machine string and stays Latin: `/settings`
 * renders `120`, never `١٢٠`, because a service parses it.
 */

const HUMAN_LOCALE = 'ar-YE';

const humanNumber = new Intl.NumberFormat(HUMAN_LOCALE);
const humanPercent = new Intl.NumberFormat(HUMAN_LOCALE, { style: 'percent', maximumFractionDigits: 0 });

/** Human-facing count, price figure, badge, queue depth, `aria-label` text. */
export function formatNumber(value: number): string {
  return humanNumber.format(value);
}

/** Human-facing percentage. The machine half of a share bar stays ASCII. */
export function formatPercent(ratio: number): string {
  return humanPercent.format(ratio);
}

/**
 * Machine-facing figure: a `data-*` value, an `aria-valuenow`, a `--progress`
 * custom property, a hidden input, a query parameter.
 */
export function toMachineNumber(value: number): string {
  return String(value);
}

const plainDigits = new Intl.NumberFormat(HUMAN_LOCALE, { useGrouping: false });

/**
 * Re-spells a digit string in Arabic-Indic **without** grouping it.
 *
 * A mobile number, a code, and a house number are digit *strings*, not
 * quantities: `formatNumber` would group them into `٧٧٠٬١٢٣` and read as an
 * amount. Non-digit characters — a space, a dash, a `+` — pass through
 * untouched, so the caller keeps whatever grouping the value's own convention
 * uses.
 *
 * It is a **human-surface** formatter. A machine attribute, a payload field, or
 * anything a validator parses keeps ASCII.
 */
export function toArabicDigits(value: string): string {
  return value.replace(/\d/g, (digit) => plainDigits.format(Number(digit)));
}

const ARABIC_INDIC_ZERO = 0x0660;
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0;

/**
 * Folds Arabic-Indic (and the Persian extended set an Arabic keyboard layout
 * can still produce) to ASCII before validation, so an operator typing on an
 * Arabic keyboard is never rejected for the glyph their keyboard produced.
 */
export function foldDigitsToAscii(input: string): string {
  let folded = '';

  for (const character of input) {
    const code = character.codePointAt(0) ?? 0;

    if (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) {
      folded += String(code - ARABIC_INDIC_ZERO);
    } else if (code >= EXTENDED_ARABIC_INDIC_ZERO && code <= EXTENDED_ARABIC_INDIC_ZERO + 9) {
      folded += String(code - EXTENDED_ARABIC_INDIC_ZERO);
    } else {
      folded += character;
    }
  }

  return folded;
}
